<template>
  <BaseCard class="flex h-125 flex-col gap-2 p-4 md:p-6">
    <!-- Header -->
    <div>
      <TextSubtitle>Day-aheadprijzen</TextSubtitle>
      <TextDescription>Huidige prijs: € {{ currentPrice }}</TextDescription>
    </div>
    <LoadingWrapper :error="loadingFailed" :loading="loading">
      <!-- Stat row -->
      <div class="flex items-start justify-between">
        <!-- Stat average -->
        <StatComponent
          label="Gemiddelde prijs"
          :value="avgPrice"
          :precision="2"
          format="€ _"
          class="flex-1"
          small
        >
          <template #icon>
            <BaseIcon name="arrow-up-down" class="fill-finance" />
          </template>
        </StatComponent>
        <!-- Stat minimum -->
        <StatComponent
          label="Laagste prijs"
          :value="minPrice"
          :precision="2"
          format="€ _"
          class="flex-1"
          small
        >
          <template #icon>
            <BaseIcon name="arrow-down" class="fill-finance" />
          </template>
        </StatComponent>
        <!-- Stat maximum -->
        <StatComponent
          label="Hoogste prijs"
          :value="maxPrice"
          :precision="2"
          format="€ _"
          class="flex-1"
          small
        >
          <template #icon>
            <BaseIcon name="arrow-up" class="fill-finance" />
          </template>
        </StatComponent>
      </div>
      <!-- Chart -->
      <TimeseriesChart
        chart-type="step"
        :series="series"
        :axes="axes"
        :data="chartData"
        :x-limits="[dateRange[0], endOfDay(dateRange[1])]"
      />
    </LoadingWrapper>
  </BaseCard>
</template>

<script setup>
import { ref, computed, watch } from "vue"
import { endOfDay } from "date-fns"
import { formatNumber } from "@/helpers/formatter.js"
import { debounce, DEBOUNCE_DELAY } from "@/helpers/debounce.js"
import useMarketPricesStore from "@/stores/repower/marketPricesStore.js"
import useNotificationStore from "@/stores/notificationStore.js"
import {
  BaseCard,
  BaseIcon,
  TextSubtitle,
  TextDescription,
  LoadingWrapper,
  TimeseriesChart,
} from "@repowerednl/ui-component-library"
import StatComponent from "@/components/information/StatComponent.vue"

const props = defineProps({
  dateRange: {
    type: Array,
    required: true,
  },
})

const LAST_ENTRY = -1
const DEFAULT_DECIMAL_PRECISION = 2
const marketPricesStore = useMarketPricesStore()
const notificationStore = useNotificationStore()
const chartData = ref({})
const loading = ref(true)
const loadingFailed = ref(false)

const series = {
  price: {
    label: "Day-ahead",
    axis: "finance",
    color: "finance",
  },
}

const axes = {
  finance: {
    title: "Prijs per MWh",
    format: "€ _",
    precision: 0,
  },
}

const currentPrice = computed(() => {
  const { data } = marketPricesStore.dayAheadPrices
  if (!data || typeof data !== "object") {
    return "-"
  }
  const entry = Object.values(data).at(LAST_ENTRY)
  if (!entry || typeof entry !== "object" || entry.price == null) {
    return "-"
  }
  return formatNumber(entry.price, DEFAULT_DECIMAL_PRECISION)
})

const avgPrice = computed(() => {
  const { data } = marketPricesStore.dayAheadPrices
  if (!data) {
    return NaN
  }
  const prices = Object.values(data).map((value) => value.price)
  return prices.reduce((total, price) => total + price, 0) / prices.length
})

const minPrice = computed(() => {
  const { data } = marketPricesStore.dayAheadPrices
  if (!data) {
    return NaN
  }
  const prices = Object.values(data).map((value) => value.price)
  const result = Math.min(...prices)
  return isFinite(result) ? result : NaN
})

const maxPrice = computed(() => {
  const { data } = marketPricesStore.dayAheadPrices
  if (!data) {
    return NaN
  }
  const prices = Object.values(data).map((value) => value.price)
  const result = Math.max(...prices)
  return isFinite(result) ? result : NaN
})

const loadData = debounce((range) => {
  marketPricesStore.loadDayAheadPrices(range, onNewPrices, onLoadPricesFailed)
}, DEBOUNCE_DELAY)

function onNewPrices(data) {
  chartData.value = data
  loading.value = false
}

function onLoadPricesFailed(error) {
  loadingFailed.value = true
  notificationStore.pushError(
    "Fout bij het ophalen van day ahead prijzen",
    `De day ahead prijzen konden niet worden opgehaald. Probeer het later opnieuw. (code: ${error.code})`,
    "load-dayahead-market-error",
  )
}

watch(
  () => props.dateRange,
  (newRange) => {
    loading.value = true
    loadData(newRange)
  },
  { immediate: true },
)
</script>
